import { useEffect, useRef, useState, ReactElement } from 'react'
import { IStoreCard } from 'src/services/MultiSellerServicesProvider'
import {
  InterchangeRequest,
  SearchFilterModel,
} from 'src/store/models/SearchModels'
import { ResultsStoreContext, ResultStore } from '../store/ResultsStore'
import { CatalogDiagramDetailDrawer } from './components/CatalogDiagramDetailDrawer'
import Filters from './components/Filters'
import LoadingMessage from './components/interchange/LoadingMessage'
import ReadyMessage from './components/interchange/ReadyMessage'
import { ResultsContent } from './components/ResultsContent'
import { SelectPartsCatalogDiagramDrawer } from './components/SelectPartsCatalogDiagramDrawer'
import styles from './results.module.scss'
import { useObserver } from 'mobx-react-lite'
import { NonCachedSearchType } from 'interfaces'
import { MultiPartInquiryCartSelector } from './components/MultiPartInquiryCartSelector'

interface IResultsProps {
  partsBinId: string
  searchType: NonCachedSearchType
  sellerDetails?: IStoreCard
  initialInterchangeRequest?: InterchangeRequest
  // TODO: Getting the interchange filters from patent for now.
  // Get the filters from the `priceAndAvailability` once the work on heavy duty is resumed.
  initialInterchangeFilters?: SearchFilterModel[]
  isGfx: boolean
  gfxSelections: string[]
  lookupTitle: string
  isLoading: boolean
}

export function Results(props: IResultsProps): ReactElement {
  const {
    partsBinId,
    searchType,
    sellerDetails,
    initialInterchangeRequest,
    initialInterchangeFilters,
    isGfx,
    gfxSelections = [],
    lookupTitle = '',
    isLoading,
  } = props

  const resultsStore = useRef<ResultStore>(new ResultStore())
  const [showFilters, setShowFilters] = useState<boolean>(false)

  useEffect(() => {
    if (sellerDetails) {
      resultsStore.current.setSellerDetails(sellerDetails)
    }
  }, [sellerDetails])

  useEffect(() => {
    // If the main lookup is being performed, display loader here.
    if (isLoading) {
      resultsStore.current.isLoading = true
    }
    // if the parent is not loading and partsbin is not present, load default data to display the no results screen.
    if (!partsBinId && !isLoading) {
      resultsStore.current.isLoading = false
      resultsStore.current.searchResults = {
        parts: [],
        filters: [],
        filteredCount: 0,
      }
      resultsStore.current.lastNonCachedSearch = searchType
      return
    }
    if (!partsBinId) {
      return
    }
    // Wait for the filters to be populated before fetching parts in case of interchange.
    if (
      searchType === NonCachedSearchType.searchByInterchange &&
      initialInterchangeFilters?.length === 0
    ) {
      return
    }
    if (!isLoading) {
      resultsStore.current.loadResultsFromPartsbin(
        partsBinId,
        searchType,
        initialInterchangeRequest,
        initialInterchangeFilters
      )
    }
  }, [
    initialInterchangeFilters,
    initialInterchangeRequest,
    partsBinId,
    searchType,
    isLoading,
  ])

  useEffect(() => {
    resultsStore.current.gfxLookupMade = isGfx
    resultsStore.current.gfxSelections = gfxSelections
    resultsStore.current.lookupTitle = lookupTitle
    if (isGfx && gfxSelections.length) {
      resultsStore.current.searchByGfxPartBin(0)
    }
  }, [isGfx, gfxSelections, lookupTitle])

  const totalResults = useObserver(() => {
    return resultsStore.current.searchResults.filteredCount || 0
  })

  return (
    <ResultsStoreContext.Provider value={resultsStore.current}>
      <div className={styles['container']}>
        <Filters visible={showFilters} setVisible={setShowFilters} />
        <ResultsContent onClickFilters={() => setShowFilters(true)} />
      </div>
      <SelectPartsCatalogDiagramDrawer />
      <CatalogDiagramDetailDrawer />
      <MultiPartInquiryCartSelector />

      {totalResults >= 0 && <LoadingMessage totalResults={totalResults} />}
      {totalResults >= 0 && <ReadyMessage />}
    </ResultsStoreContext.Provider>
  )
}

export default Results
